import React from 'react';
import KPICard from './KPICard.jsx';
import MoneyCell from './MoneyCell.jsx';
import ProvenanceChip from './ProvenanceChip.jsx';
import SectionCard from './SectionCard.jsx';
import {
  FROZEN_LEDGER_PROJECTION_RESPONSE,
  FROZEN_PROJECTION_ACCEPTANCE_VALUES,
  FROZEN_PROJECTION_SOURCE_CHIPS,
} from '../fixtures/frozen-ledger-projection-response.js';

const KPI_GRID = 'grid grid-cols-1 gap-3 min-w-0 sm:grid-cols-[repeat(auto-fit,minmax(165px,1fr))]';
const CHIP_ROW = 'flex flex-wrap gap-2 min-w-0';
const NOTE = 'text-[0.78rem] text-text-muted leading-[1.55] m-0';
const PANEL_GRID = 'grid grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)] gap-4 max-[980px]:grid-cols-1';
const STACK = 'flex min-w-0 flex-col gap-3';

function chipToneClass(tone) {
  if (tone === 'good') return 'bg-[rgba(74,222,128,0.10)] text-accent-green border-[rgba(74,222,128,0.35)]';
  if (tone === 'warn') return 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]';
  if (tone === 'bad') return 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]';
  return 'bg-surface-el text-text-muted border-border';
}

function EvidenceChip({ label, value, tone }) {
  return (
    <span className={`inline-flex max-w-full min-w-0 shrink-0 flex-wrap items-center gap-x-1 gap-y-0.5 rounded-sm border px-2 py-1 text-[0.68rem] font-semibold leading-snug ${chipToneClass(tone)}`}>
      <span className="shrink-0 uppercase tracking-[0.08em] text-current opacity-75">{label}</span>
      <span className="min-w-0 break-words text-text">{value}</span>
    </span>
  );
}

function GuardrailRow({ label, value, tone }) {
  return (
    <li className="flex min-w-0 items-center justify-between gap-3 border-b border-dashed border-border py-1.5 last:border-b-0">
      <span className="min-w-0 break-words text-[0.8rem] text-text-muted">{label}</span>
      <span className={`max-w-[55%] rounded-sm border px-2 py-0.5 text-right text-[0.68rem] font-semibold uppercase tracking-[0.08em] break-words ${chipToneClass(tone)}`}>
        {value}
      </span>
    </li>
  );
}

function WarningGroup({ warning }) {
  return (
    <li className="rounded-sm border border-border bg-surface-el px-3 py-2">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <strong className="text-[0.78rem] uppercase tracking-[0.08em] text-text">{warning.category}</strong>
        <span className={`rounded-sm border px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.08em] ${chipToneClass(warning.tone)}`}>
          {warning.state}
        </span>
      </div>
      <p className="m-0 text-[0.76rem] leading-[1.45] text-text-muted">{warning.detail}</p>
    </li>
  );
}

export default function LedgerProjectionClarityPanel() {
  const response = FROZEN_LEDGER_PROJECTION_RESPONSE;
  const { projection, proof, guardrails, warningGroups, labState } = response;
  const income = projection.projectedIncomeStatement;
  const cash = projection.projectedCashFlow;
  const balance = projection.projectedBalanceSheet;
  const checks = projection.balanceChecks;

  const kpis = [
    { label: 'Projected revenue', value: income.revenue, tone: 'good', sub: 'proof matrix revenue' },
    { label: 'Projected expenses', value: income.expenses, tone: 'warn', sub: 'proof matrix expense total' },
    { label: 'Projected net income', value: income.netIncome, tone: 'good', sub: 'revenue minus expenses' },
    { label: 'Cash inflow', value: cash.cashInflow, tone: 'good', sub: 'synthetic operating inflow' },
    { label: 'Cash outflow', value: cash.cashOutflow, tone: 'warn', sub: 'synthetic operating outflow' },
    { label: 'Ending cash', value: cash.endingCash, tone: 'good', sub: 'three-month horizon end' },
    { label: 'Net fixed assets', value: balance.netFixedAssets, tone: 'neutral', sub: 'after fixture depreciation' },
    { label: 'Ending assets', value: balance.totalAssets, tone: 'good', sub: 'cash plus net fixed assets' },
    { label: 'Accounts payable', value: balance.accountsPayable, tone: 'warn', sub: 'synthetic AP accrual' },
    { label: 'Ending equity', value: balance.equity, tone: 'good', sub: 'opening equity plus earnings' },
  ];

  const guardrailRows = [
    ['read-only', guardrails.readOnly ? 'true' : 'false', guardrails.readOnly ? 'good' : 'bad'],
    ['tenant-scoped', guardrails.tenantScoped ? 'true' : 'false', guardrails.tenantScoped ? 'good' : 'bad'],
    ['active-book authority', guardrails.bookAuthority === 'active_tenant_context_book_id' ? 'true' : 'false', guardrails.bookAuthority === 'active_tenant_context_book_id' ? 'good' : 'bad'],
    ['body book id ignored', guardrails.bodyBookIdIgnored ? 'true' : 'false', guardrails.bodyBookIdIgnored ? 'good' : 'bad'],
    ['cross-book hard-stop', guardrails.crossBookRowsHardStopBeforeCalculation ? 'true' : 'false', guardrails.crossBookRowsHardStopBeforeCalculation ? 'good' : 'bad'],
    ['writes Ledger', guardrails.writesLedger ? 'true' : 'false', guardrails.writesLedger ? 'bad' : 'good'],
    ['writes Forecasts state', guardrails.writesForecastScenario ? 'true' : 'false', guardrails.writesForecastScenario ? 'bad' : 'good'],
    ['writes Budgets state', guardrails.writesBudget ? 'true' : 'false', guardrails.writesBudget ? 'bad' : 'good'],
    ['writes Lens storage', guardrails.writesLensStorage ? 'true' : 'false', guardrails.writesLensStorage ? 'bad' : 'good'],
    ['uses provider', guardrails.usesProvider ? 'true' : 'false', guardrails.usesProvider ? 'bad' : 'good'],
    ['uses real finance data', guardrails.usesRealFinanceData ? 'true' : 'false', guardrails.usesRealFinanceData ? 'bad' : 'good'],
  ];

  return (
    <SectionCard
      sectionId="ledger-projection"
      title="Ledger Projection Clarity Panel"
      subtitle="Frozen fake Ledger response; source-only Lens boundary proof."
      right={<ProvenanceChip kind="frozen-local-fake" tooltip="Imported from a local frozen fixture. No route, provider, DB, storage, or Ledger call." />}
    >
      <div className={STACK}>
        <div className={CHIP_ROW}>
          {FROZEN_PROJECTION_SOURCE_CHIPS.map((chip) => (
            <EvidenceChip key={chip.label} {...chip} />
          ))}
        </div>

        <div className={PANEL_GRID}>
          <div className={STACK}>
            <div className={KPI_GRID}>
              {kpis.map((item) => (
                <KPICard
                  key={item.label}
                  label={item.label}
                  value={<MoneyCell value={item.value} decimals={0} tone={item.tone === 'neutral' ? undefined : item.tone} label={item.label} />}
                  tone={item.tone === 'neutral' ? undefined : item.tone}
                  sub={item.sub}
                  provenance="frozen-local-fake"
                  provenanceTip="Frozen local Ledger projection fixture; fake evidence only."
                />
              ))}
            </div>

            <div className="rounded-sm border border-border bg-surface-el px-3 py-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <strong className="text-[0.82rem] text-text">Accepted proof matrix</strong>
                <EvidenceChip
                  label="balance"
                  value={checks.balanceSheetBalanced ? 'balanced true' : 'unbalanced'}
                  tone={checks.balanceSheetBalanced ? 'good' : 'bad'}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 text-[0.78rem] text-text-muted sm:grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
                <span>Revenue: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.projectedRevenue}</span>
                <span>Expenses: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.projectedExpenses}</span>
                <span>Net income: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.projectedNetIncome}</span>
                <span>Cash inflow: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.projectedCashInflow}</span>
                <span>Cash outflow: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.projectedCashOutflow}</span>
                <span>Ending cash: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.endingCash}</span>
                <span>Net fixed assets: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.endingNetFixedAssets}</span>
                <span>Assets: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.endingAssets}</span>
                <span>Accounts payable: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.endingAccountsPayable}</span>
                <span>Equity: {FROZEN_PROJECTION_ACCEPTANCE_VALUES.endingEquity}</span>
                <span>Difference: {checks.difference}</span>
              </div>
            </div>
          </div>

          <div className={STACK}>
            <div className="rounded-sm border border-border bg-surface px-3 py-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <strong className="text-[0.82rem] text-text">Guardrails</strong>
                <EvidenceChip label="proof" value={proof.proofMatrixAccepted ? 'accepted local' : 'not accepted'} tone={proof.proofMatrixAccepted ? 'good' : 'bad'} />
              </div>
              <ul className="m-0 list-none p-0">
                {guardrailRows.map(([label, value, tone]) => (
                  <GuardrailRow key={label} label={label} value={value} tone={tone} />
                ))}
              </ul>
            </div>

            <div className="rounded-sm border border-border bg-surface px-3 py-3">
              <strong className="mb-2 block text-[0.82rem] text-text">Lab state</strong>
              <div className={CHIP_ROW}>
                <EvidenceChip label="local" value={labState.local} tone="good" />
                <EvidenceChip label="Vercel" value={labState.vercel} tone="warn" />
                <EvidenceChip label="Supabase" value={labState.supabase} tone="bad" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-border bg-surface px-3 py-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <strong className="text-[0.82rem] text-text">Warning categories</strong>
            <span className="text-[0.72rem] text-text-muted">{warningGroups.length} categories visible</span>
          </div>
          <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
            {warningGroups.map((warning) => (
              <WarningGroup key={warning.category} warning={warning} />
            ))}
          </ul>
        </div>

        <p className={NOTE}>
          Inspection-only. No finance, tax, legal, funding, staffing, pricing, filing, cash-transfer, or operating action is generated.
          Provider confidence remains limited until hosted lab proof clears the Supabase blocker.
        </p>
      </div>
    </SectionCard>
  );
}
